<script setup lang="ts">
	import type { IconLibProps } from '~/components/Tools/icon.vue'

	export type UploadTypeResponse = {
		file: File
		files: File[]
	}
	const attr = useAttrs()
	const file = ref<null | File>()
	const inputEl = ref<null | HTMLInputElement>()
	const uploadFailed = ref(false)
	const dragOverActive = ref(false)
	const isCamera = shallowRef(false)
	const errors = ref<string[]>([])
	const props = defineProps<{
		id?: string
		placeholder?: string
		placeholderIcon?: IconLibProps
		value?: string | File
		uploading?: boolean
		name?: string
		isDocument?: boolean
		// This prop is an alternative of accept attribute
		uploadTypes?: string
		capturType?: boolean | 'user' | 'environment' | undefined
		activeWrapperClass?: ClassContext
		useCamera?: boolean
	}>()
	const emits = defineEmits<{
		(param: 'dragOver', arg: Event): void
		(param: 'error', arg: string[]): void
		(param: 'update:value', arg: string | File): void
		(param: 'change', arg: UploadTypeResponse): void
	}>()
	const imgFormats = ['image/png', 'image/jpg', 'image/jpeg', 'image/gif', 'image/webp', 'image/ico']
	const removeFile = () => {
		file.value = null
		emits('update:value', '')
	}
	const isValidFormat = (file: File) => {
		const validInputAttrs = attr as unknown as HTMLInputElement
		const acceptedFormats = (props.uploadTypes || validInputAttrs?.accept)?.trim().replace(/\./g, '').split(',')
		const fileFormat = file.type?.split('/')[1] || ''

		const isValid = acceptedFormats?.includes(fileFormat)
		if (props.isDocument) {
			return true
		}
		return (validInputAttrs.accept || props.uploadTypes) && isValid
	}
	const openExplorer = () => {
		if (inputEl.value) {
			inputEl.value?.click()
		}
		isCamera.value = false
	}
	const openFile = () => {
		if (props.uploading) {
			return null
		}
		if (props.value) {
			removeFile()
			return null
		}
		if (props.useCamera) {
			isCamera.value = true
			return null
		}
		openExplorer()
	}
	const dragLeave = () => {
		dragOverActive.value = false
	}
	const dragOver = (e: Event) => {
		dragOverActive.value = true
		emits('dragOver', e)
	}

	const fileDropped = (e: DragEvent) => {
		fetchFile({ target: e.dataTransfer } as unknown as Event)
		dragOverActive.value = false
	}

	function fetchFile(e: Event | null, cameraFile?: File) {
		// Prevent file explorer from opening if the file is uploading or is present
		if (props.value || props.uploading) {
			return null
		}
		uploadFailed.value = false
		const target = e?.target as HTMLInputElement
		const files = (target?.files as unknown as File[]) || []
		errors.value = []

		if (files || cameraFile) {
			if (
				(props.useCamera && cameraFile && ![cameraFile].every(f => isValidFormat(f))) ||
				((Boolean(attr?.accept) || props.uploadTypes) && !Array.from(files).every(f => isValidFormat(f)))
			) {
				errors.value.push('Error, file format not supported')
				emits('error', errors.value)
			} else {
				const selectedFile = cameraFile || files[0]
				if (selectedFile) {
					file.value = selectedFile
					emits('update:value', selectedFile)
					emits('change', { file: selectedFile, files: cameraFile ? [cameraFile] : Array.from(files) })
				}
			}
		}
		if (e) {
			// Reset the input value to enable you reselect same file
			target.value = ''
		}
	}
	const getFileTypeName = (file: string | File) => {
		if (typeof file === 'string') {
			return file
		}
		return file.name
	}
	watch(errors, errorList => {
		for (let index = 0; index < errorList.length; index++) {
			useNotification().alert({
				status: 400,
				message: errorList[index] || ''
			})
		}
	})
</script>

<template>
	<div
		class="relative mx-auto flex min-h-[120px] w-full flex-col items-center justify-center rounded-lg border-[2px] border-dotted border-gray-300 bg-gray-50 transition-all hover:border-gray-400"
		:class="[
			{
				'border-gray-400': dragOverActive,
				'cursor-progress': uploading,
				'cursor-pointer': !uploading
			},
			(Boolean(value) && activeWrapperClass) || ''
		]"
		@click="openFile()"
		@dragleave.stop.prevent="dragLeave"
		@dragover.stop.prevent="dragOver"
		@drop.stop.prevent="fileDropped"
	>
		<div v-if="uploading" class="absolute right-[3px] top-5 sm:right-5">
			<a-spin spinning size="small" />
		</div>

		<div v-if="!value" class="">
			<div class="flex w-full flex-col items-center justify-center gap-3 text-center">
				<lazy-tools-icon dir="system" icon="export" path-class="stroke-gray-600" v-bind="placeholderIcon" />
				<div class="max-w-lg text-sm font-medium leading-[18px] tracking-wide text-gray-500">
					<span
						v-show="!!placeholder"
						v-html="placeholder?.replace('file', `<span class='text-primary-2 '>Choose File</span>`)"
					/>
					<p v-show="!placeholder" class="">
						Drag & drop or
						<span class="text-primary-2"> choose file</span>
						to upload.
						<br />
						XLS
					</p>
				</div>
			</div>
		</div>

		<div v-else-if="Boolean(value)" class="">
			<div class="flex w-full flex-col items-center justify-center gap-3 text-center">
				<lazy-tools-icon
					:dir="
						(typeof value === 'string' && !imgFormats.includes(value)) ||
						(typeof value !== 'string' && !imgFormats.includes(value?.type))
							? 'documents'
							: 'media'
					"
					:icon="
						(typeof value === 'string' && !imgFormats.includes(value)) ||
						(typeof value !== 'string' && !imgFormats.includes(value?.type))
							? 'pdf'
							: 'gallery'
					"
					:height="24"
				/>
				<div class="max-w-lg text-sm font-medium leading-[18px] tracking-wide text-gray-500">
					<div class="flex items-center gap-x-1 gap-y-3">
						<a-tooltip placement="bottom">
							<span
								class="max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap text-gray-600"
								:title="getFileTypeName(value)"
							>
								{{ name || getFileTypeName(value) }}
							</span>
							<template v-if="value" #title>
								<span class="text-sm text-white">{{ name || getFileTypeName(value) }}</span>
							</template>
						</a-tooltip>

						<button type="button" aria-roledescription="remove file" class="p-2" @click.stop="removeFile">
							<lazy-tools-icon
								icon="trash"
								dir="other"
								:height="16"
								:width="16"
								path-class="stroke-error-500"
							/>
						</button>
					</div>
				</div>
				<lazy-tools-icon
					v-if="activeWrapperClass"
					dir="fill"
					icon="check-2"
					:width="18"
					class="absolute right-3 top-2"
				/>
			</div>
		</div>
		<input
			:id="id"
			ref="inputEl"
			type="file"
			name="inputElFile"
			class="!hidden"
			:capture="capturType || 'user'"
			v-bind="{ ...attr, class: '', style: '' }"
			@change="fetchFile"
		/>
		<media-camera-input
			v-model:show="isCamera"
			@change="file => fetchFile(null, file)"
			@not-supported="openExplorer()"
		/>
	</div>
</template>
